// Behavioural port of AI SDK Elements' Reasoning (Apache-2.0, Copyright Vercel); interaction pattern only.

import { useDisclosure, type UseDisclosure } from '../../../composables/useDisclosure'
import { ref, computed, watch, type ComputedRef, type Ref } from 'vue'

export interface UseActivityDisclosureOptions {
  /** Initial open state in uncontrolled mode; folded when absent. */
  defaultOpen?: boolean
  open?: Ref<boolean>
  onOpenChange?: (open: boolean) => void
  duration?: Ref<number | undefined>
}

export interface UseActivityDisclosure extends UseDisclosure {
  /** Elapsed streaming time in seconds: `opts.duration` if given, else auto-measured. */
  resolvedDuration: ComputedRef<number | undefined>
}

/** Open state of an activity thread: it opens and closes only on the reader's choice, and measures how long the run streamed. */
export function useActivityDisclosure(
  isStreaming: Ref<boolean>,
  opts: UseActivityDisclosureOptions = {},
): UseActivityDisclosure {
  const disclosure = useDisclosure({
    defaultOpen: opts.defaultOpen ?? false,
    open: opts.open,
    onOpenChange: opts.onOpenChange,
  })

  let startTime: number | null = isStreaming.value ? Date.now() : null
  const computedDuration = ref<number | undefined>(undefined)

  watch(isStreaming, (streaming, prev) => {
    if (streaming) {
      if (startTime === null) startTime = Date.now()
    } else if (prev && startTime !== null) {
      computedDuration.value = Math.max(1, Math.ceil((Date.now() - startTime) / 1000))
      startTime = null
    }
  })

  const resolvedDuration = computed(() => opts.duration?.value ?? computedDuration.value)

  return { ...disclosure, resolvedDuration }
}
